import { computed } from 'vue';
import { useStates } from '../router/states';
import { useDynamicPermissions } from './useDynamicPermissions';
import { PermissionFlag } from '../types/permissions';

/**
 * Módulos disponibles en el sistema
 */
export enum ModuleCode {
  GLOBAL = 'GLOBAL',
  SALES = 'SALES',
  PURCHASES = 'PURCHASES',
  INVENTORY = 'INVENTORY',
  CONFIGURATION = 'CONFIGURATION',
  BUSINESS_MODULE = 'BUSINESS_MODULE',
  USERS = 'USERS',
  ACCOUNTING = 'ACCOUNTING',
}

/**
 * Composable para verificar acceso a módulos según permisos dinámicos
 * 
 * IMPORTANTE: Usa permisos dinámicos de la BD en lugar de sessionStorage
 */
export function useModuleAccess() {
  const { dynamicPermissionsState } = useStates();
  const { hasPermission } = useDynamicPermissions();

  /**
   * Obtiene los módulos permitidos desde permisos dinámicos
   */
  const getAllowedModules = (): string[] => {
    try {
      // Si los permisos dinámicos están cargados, usarlos
      if (dynamicPermissionsState.isLoaded.value && dynamicPermissionsState.permissions.value.length > 0) {
        return dynamicPermissionsState.permissions.value
          .filter(p => p.active && p.permissions > 0)
          .map(p => p.moduleCode);
      }
      
      // Fallback: usar sessionStorage si los permisos dinámicos no están cargados
      const modulesJson = sessionStorage.getItem('allowedModules');
      if (!modulesJson) {
        // Si no hay módulos guardados, permitir acceso completo (desarrollo/testing)
        return Object.values(ModuleCode);
      }
      return JSON.parse(modulesJson) as string[];
    } catch (error) {
      console.error('Error getting allowed modules:', error);
      // En caso de error, permitir acceso completo
      return Object.values(ModuleCode);
    }
  };

  /**
   * Verifica si el usuario tiene acceso a un módulo específico
   */
  const hasModuleAccess = (moduleCode: ModuleCode | string): boolean => {
    // GLOBAL siempre está permitido
    if (moduleCode === ModuleCode.GLOBAL) {
      return true;
    }
    
    // Si los permisos dinámicos están cargados, usarlos
    if (dynamicPermissionsState.isLoaded.value) {
      // Verificar si tiene al menos READ en el módulo
      return hasPermission(moduleCode, PermissionFlag.READ);
    }
    
    // Fallback: usar sessionStorage
    const allowedModules = getAllowedModules();
    return allowedModules.includes(moduleCode);
  };

  /**
   * Verifica si el usuario tiene acceso a al menos uno de los módulos especificados
   */
  const hasAnyModuleAccess = (moduleCodes: (ModuleCode | string)[]): boolean => {
    return moduleCodes.some((code) => hasModuleAccess(code));
  };

  /**
   * Verifica si el usuario tiene acceso a todos los módulos especificados
   */
  const hasAllModulesAccess = (moduleCodes: (ModuleCode | string)[]): boolean => {
    return moduleCodes.every((code) => hasModuleAccess(code));
  };

  /**
   * Computed para verificar acceso a módulos específicos
   */
  const canAccessSales = computed(() => hasModuleAccess(ModuleCode.SALES));
  const canAccessPurchases = computed(() => hasModuleAccess(ModuleCode.PURCHASES));
  const canAccessInventory = computed(() => hasModuleAccess(ModuleCode.INVENTORY));
  const canAccessAccounting = computed(() => hasModuleAccess(ModuleCode.ACCOUNTING));
  const canAccessConfiguration = computed(() => hasModuleAccess(ModuleCode.CONFIGURATION));
  const canAccessBusinessModule = computed(() => hasModuleAccess(ModuleCode.BUSINESS_MODULE));
  const canAccessUsers = computed(() => hasModuleAccess(ModuleCode.USERS));

  /**
   * Lista de módulos permitidos (reactivo)
   */
  const allowedModules = computed(() => getAllowedModules());

  return {
    // Funciones
    hasModuleAccess,
    hasAnyModuleAccess,
    hasAllModulesAccess,
    getAllowedModules,
    
    // Computed helpers
    canAccessSales,
    canAccessPurchases,
    canAccessInventory,
    canAccessAccounting,
    canAccessConfiguration,
    canAccessBusinessModule,
    canAccessUsers,
    allowedModules,
    
    // Enum para uso externo
    ModuleCode,
  };
}
